import { useQuery } from '@tanstack/react-query';

import type { ConsultationModel } from '@/api/consultations/Consultation.types';
import { getConsultationStatus } from '@/api/consultations/getConsultationStatus';
import { mapConsultationStatuses } from '@/api/consultations/response-mappers';
import {
  FF_AWAITING_DOCUMENT_DOCTOR_CONSULT_STATUS,
  FF_ENABLE_CANCEL_INSTANT_CONSULTATION,
} from '@/constants/featureFlags';
import { QueryKey } from '@/data/query';
import { useFeatureFlags } from '@/hooks';

export type ConsultationStatusMapped = {
  name: string;
  value: number;
};

type UseGetConsultationStatusResponse = {
  consultationStatuses: ConsultationStatusMapped[];
  isError: boolean;
  isLoading: boolean;
};

export const useGetConsultationStatus = (
  showConsultation: ConsultationModel | null,
): UseGetConsultationStatusResponse => {
  const { flags } = useFeatureFlags();
  const ffEnableCancelInstantConsultation = flags[FF_ENABLE_CANCEL_INSTANT_CONSULTATION];
  const ffAwaitingDocumentDoctorConsultStatus: boolean = flags[FF_AWAITING_DOCUMENT_DOCTOR_CONSULT_STATUS];

  const {
    data: consultationStatuses,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [QueryKey.GetConsultationStatus],
    enabled: !!showConsultation,
    queryFn: async () => {
      const consultationStatusResponse = await getConsultationStatus(
        showConsultation?.id,
        ffEnableCancelInstantConsultation,
      );
      return mapConsultationStatuses(
        showConsultation!,
        consultationStatusResponse,
        ffAwaitingDocumentDoctorConsultStatus,
      );
    },
  });

  return {
    consultationStatuses: consultationStatuses!,
    isError,
    isLoading,
  };
};
